import { useEffect } from 'react';

import { FormattedMessage, InjectedIntl, injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import CodeIcon from '@atlaskit/icon/glyph/code';
import EditIcon from '@atlaskit/icon/glyph/edit';
import IssuesIcon from '@atlaskit/icon/glyph/issues';
import LinkIcon from '@atlaskit/icon/glyph/link';
import {
  useCrossFlow,
  Journeys,
  Targets,
} from '@atlassiansox/cross-flow-support';

import Loading from 'src/components/loading';
import { LoadingStatus } from 'src/constants/loading-status';
import { initJiraRepoPage } from 'src/redux/jira/actions';
import { WorkspacePermission } from 'src/redux/workspaces/constants';
import {
  publishUiEvent,
  publishScreenEvent,
} from 'src/utils/analytics/publish';

import { SCREEN_NAME } from './constants';
import PromoComponent from './empty-state-promo';
import emptyStateTryScreen1ImgUrl from './empty-state-try-screen-1.png';
import emptyStateTryScreen2VideoUrl from './empty-state-try-screen-2.mp4';
import emptyStateTryScreen2ImgUrl from './empty-state-try-screen-2.png';
import emptyStateTryScreen3VideoUrl from './empty-state-try-screen-3.mp4';
import emptyStateTryScreen3ImgUrl from './empty-state-try-screen-3.png';
import emptyStateTryScreen4ImgUrl from './empty-state-try-screen-4.png';
import messages from './empty-state.i18n';
import * as styles from './empty-state.styled';

type PrimaryActionProps = {
  workspacePermissionFetchedStatus: LoadingStatus;
  workspacePermission?: WorkspacePermission;
  repositoryFullSlug: string;
};

const PrimaryAction = ({
  workspacePermissionFetchedStatus,
  workspacePermission,
  repositoryFullSlug,
}: PrimaryActionProps) => {
  const crossFlow = useCrossFlow();
  const dispatch = useDispatch();

  const handleTryJiraFreeButtonClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'tryJiraFreeButton',
      source: 'repositoryJiraScreen',
    });

    if (crossFlow.isEnabled) {
      crossFlow.api
        .open({
          journey: Journeys.GET_STARTED,
          targetProduct: Targets.JIRA_SOFTWARE,
          sourceComponent: 'repository-jira-issues-tab',
          sourceContext: 'recommendation',
        })
        .then(() => {
          // This promise gets resolved when the cross flow screen is closed.
          // User may have signed up for a new site we need to re-initialize the
          // the screen to see what is changed.
          dispatch(initJiraRepoPage(repositoryFullSlug));
        });
    }
  };

  if (workspacePermissionFetchedStatus === LoadingStatus.Success) {
    return (
      <div>
        <CustomThemeButton
          appearance="primary"
          onClick={handleTryJiraFreeButtonClick}
          isDisabled={workspacePermission !== WorkspacePermission.Owner}
          testId="try-jira-button"
        >
          <FormattedMessage {...messages.tryJiraFreeButton} />
        </CustomThemeButton>
        <styles.TertiaryFooterText>
          {workspacePermission !== WorkspacePermission.Owner && (
            <FormattedMessage {...messages.tertiaryTextTryNonAdmin} />
          )}
        </styles.TertiaryFooterText>
      </div>
    );
  } else if (workspacePermissionFetchedStatus === LoadingStatus.Fetching) {
    return <Loading size="small" />;
  } else if (workspacePermissionFetchedStatus === LoadingStatus.Failed) {
    // TODO
  }

  return null;
};

export type JiraPageEmptyStateTryProps = {
  intl: InjectedIntl;
  workspacePermission?: WorkspacePermission;
  workspacePermissionFetchedStatus: LoadingStatus;
  repositoryFullSlug: string;
};

const JiraPageEmptyStateTry = (props: JiraPageEmptyStateTryProps) => {
  const {
    intl,
    workspacePermission,
    workspacePermissionFetchedStatus,
    repositoryFullSlug,
  } = props;

  const promoItems = [
    {
      icon: <IssuesIcon label="Issues icon" size="medium" />,
      title: intl.formatMessage(messages.tryJiraPromoButton1Title),
      description: intl.formatMessage(messages.tryJiraPromoButton1Details),
      url: emptyStateTryScreen1ImgUrl,
    },
    {
      icon: <LinkIcon label="Link icon" size="medium" />,
      title: intl.formatMessage(messages.tryJiraPromoButton2Title),
      description: intl.formatMessage(messages.tryJiraPromoButton2Description),
      url: emptyStateTryScreen2ImgUrl,
      videoUrl: emptyStateTryScreen2VideoUrl,
    },
    {
      icon: <EditIcon label="Edit icon" size="medium" />,
      title: intl.formatMessage(messages.tryJiraPromoButton3Title),
      description: intl.formatMessage(messages.tryJiraPromoButton3Description),
      url: emptyStateTryScreen3ImgUrl,
      videoUrl: emptyStateTryScreen3VideoUrl,
    },
    {
      icon: <CodeIcon label="Code icon" size="medium" />,
      title: intl.formatMessage(messages.tryJiraPromoButton4Title),
      description: intl.formatMessage(messages.tryJiraPromoButton4Description),
      url: emptyStateTryScreen4ImgUrl,
    },
  ];

  useEffect(() => {
    publishScreenEvent(SCREEN_NAME, { variation: 'emptyStateTry' });
  }, []);

  return (
    <styles.PageContainer>
      <PromoComponent promoItems={promoItems} />
      <styles.Footer>
        <styles.FooterLeft>
          <h4>
            <FormattedMessage {...messages.emptyStateTryHeader} />
          </h4>
          <styles.TertiaryFooterText>
            <FormattedMessage {...messages.tertiaryText} />
          </styles.TertiaryFooterText>
        </styles.FooterLeft>
        <styles.FooterRight>
          <PrimaryAction
            workspacePermissionFetchedStatus={workspacePermissionFetchedStatus}
            workspacePermission={workspacePermission}
            repositoryFullSlug={repositoryFullSlug}
          />
        </styles.FooterRight>
      </styles.Footer>
    </styles.PageContainer>
  );
};

export default injectIntl(JiraPageEmptyStateTry);
